import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from '../ui/Button';
import { cn } from '../../utils/cn';
import type { NavItem } from '../../types';

const navigation: NavItem[] = [
    { label: 'Home', href: '/' },
    { label: 'About', href: '/about' },
    { label: 'Demo', href: '/demo' },
    { label: 'Contact', href: '/contact' },
    { label: 'Privacy', href: '/privacy-policy' },
    { label: 'Terms', href: '/terms' },
    { label: 'Guidelines', href: '/community-guidelines' },
    { label: 'Safety', href: '/child-safety' },
];

export const Header: React.FC = () => {
    const [isOpen, setIsOpen] = useState(false);
    const location = useLocation();

    const isActive = (href: string) => {
        if (href === '/') {
            return location.pathname === '/';
        }
        return location.pathname.startsWith(href);
    };

    return (
        <header className="fixed top-0 w-full bg-white/95 backdrop-blur-md border-b border-gray-200 z-50">
            <div className="container-custom">
                <nav className="flex items-center justify-between h-16">
                    {/* Logo */}
                    <Link to="/" className="flex items-center space-x-2 group">
                        <picture>
                            <source srcSet="/assets/app_icon_512_512.webp" type="image/webp" />
                            <img
                                src="/assets/app_icon_512_512.png"
                                alt="Swag Shots Logo"
                                width="40"
                                height="40"
                                className="w-10 h-10 rounded-lg"
                            />
                        </picture>
                        <span className="text-xl font-bold text-primary-600 group-hover:text-primary-700 transition-colors">
                            Swag Shots
                        </span>
                    </Link>

                    {/* Desktop Navigation */}
                    <div className="hidden md:flex items-center space-x-8">
                        {navigation.map((item) => (
                            <Link
                                key={item.href}
                                to={item.href}
                                className={cn(
                                    'px-3 py-2 rounded-lg font-medium transition-colors',
                                    isActive(item.href)
                                        ? 'text-primary-600 bg-primary-50'
                                        : 'text-gray-600 hover:text-primary-600 hover:bg-primary-50'
                                )}
                            >
                                {item.label}
                            </Link>
                        ))}
                    </div>

                    {/* Desktop CTA */}
                    <div className="hidden md:flex items-center space-x-4">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open('https://play.google.com/store/apps/details?id=com.swagshots.app', '_blank')}
                        >
                            Download for Android
                        </Button>
                    </div>

                    {/* Mobile menu button */}
                    <button
                        onClick={() => setIsOpen(!isOpen)}
                        className="md:hidden p-2 rounded-lg text-gray-600 hover:text-primary-600 hover:bg-primary-50 transition-colors"
                    >
                        {isOpen ? <X size={24} /> : <Menu size={24} />}
                    </button>
                </nav>

                {/* Mobile Navigation */}
                {isOpen && (
                    <div className="md:hidden border-t border-gray-200 bg-white">
                        <div className="px-4 py-6 space-y-4">
                            {navigation.map((item) => (
                                <Link
                                    key={item.href}
                                    to={item.href}
                                    onClick={() => setIsOpen(false)}
                                    className={cn(
                                        'block px-3 py-2 rounded-lg font-medium transition-colors',
                                        isActive(item.href)
                                            ? 'text-primary-600 bg-primary-50'
                                            : 'text-gray-600 hover:text-primary-600 hover:bg-primary-50'
                                    )}
                                >
                                    {item.label}
                                </Link>
                            ))}
                            <div className="pt-4">
                                <Button
                                    variant="primary"
                                    size="md"
                                    className="w-full"
                                    onClick={() => {
                                        window.open('https://play.google.com/store/apps/details?id=com.swagshots.app', '_blank');
                                        setIsOpen(false);
                                    }}
                                >
                                    Download for Android
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </header>
    );
};
